// Generated from products/analytics/sdk/pageEngagement.ts; run analytics generate:site-runtime.
import { ENGAGEMENT_HEARTBEAT_MS, ENGAGEMENT_MAX_MS } from "./engagement.gen";
import type { EngagementSnapshot } from "./engagement.gen";

/** Passive lifecycle surface; embedders without a document need not provide it. */
export interface EngagementEnvironment {
  monotonicNow(): number;
  visible(): boolean;
  observe(
    listener: (reason: "hidden" | "visible" | "pagehide", persisted?: boolean) => void,
  ): () => void;
  schedule(listener: () => void, ms: number): () => void;
}
export interface EngagementObserver {
  reset(): void;
  resume(): void;
  close(): void;
}
export function observeEngagement(
  environment: EngagementEnvironment,
  uuid: () => string | null,
  prepare: () => boolean,
  emit: (snapshot: EngagementSnapshot) => void,
): EngagementObserver {
  let pageVisitId = uuid();
  let last = environment.monotonicNow();
  if (!Number.isFinite(last)) throw new Error("Monotonic timing unavailable");
  let elapsedMs = 0;
  let visibleMs = 0;
  let visible = environment.visible();
  let left = false;
  let closed = false;
  let cancel: (() => void) | null = null;
  const tick = () => {
    let at: number;
    try {
      at = environment.monotonicNow();
    } catch {
      return;
    }
    if (!Number.isFinite(at)) return;
    // A backwards monotonic test clock cannot subtract previously observed time.
    const delta = Math.max(0, at - last);
    elapsedMs = Math.min(ENGAGEMENT_MAX_MS, elapsedMs + delta);
    if (visible) visibleMs = Math.min(elapsedMs, visibleMs + delta);
    last = Math.max(last, at);
  };
  const stop = () => {
    const pending = cancel;
    cancel = null;
    try {
      pending?.();
    } catch {
      /* Cleanup must not interrupt the host. */
    }
  };
  const snapshot = (reason: EngagementSnapshot["reason"], persisted?: boolean) => {
    if (closed || !prepare() || !pageVisitId) return;
    tick();
    emit({
      pageVisitId,
      elapsedMs: Math.floor(elapsedMs),
      visibleMs: Math.floor(visibleMs),
      reason,
      ...(persisted === undefined ? {} : { persisted }),
    });
  };
  const schedule = () => {
    stop();
    if (!closed && visible && !left)
      cancel = environment.schedule(() => {
        cancel = null;
        try {
          snapshot("heartbeat");
          if (!closed) schedule();
        } catch {
          stop();
        }
      }, ENGAGEMENT_HEARTBEAT_MS);
  };
  const lifecycle = (reason: "hidden" | "visible" | "pagehide", persisted?: boolean) => {
    try {
      if (closed) return;
      if (reason === "pagehide") {
        if (left) return;
        snapshot(reason, persisted);
        visible = false;
        left = true;
        stop();
      } else if (reason === "hidden") {
        if (!visible || left) return;
        snapshot(reason);
        visible = false;
        stop();
      } else {
        if (visible && !left) return;
        // prepare may rotate an idle session and reset the page before sampling.
        if (!prepare() || closed) return;
        tick();
        visible = true;
        left = false;
        snapshot(reason);
        schedule();
      }
    } catch {
      stop();
    }
  };
  const cleanup = environment.observe(lifecycle);
  const observer: EngagementObserver = {
    resume() {
      lifecycle("visible");
    },
    reset() {
      pageVisitId = uuid();
      try {
        const at = environment.monotonicNow();
        if (Number.isFinite(at)) last = at;
      } catch {
        /* Keep the last monotonic observation. */
      }
      elapsedMs = 0;
      visibleMs = 0;
    },
    close() {
      if (closed) return;
      closed = true;
      stop();
      try {
        cleanup();
      } catch {
        /* Cleanup is best effort. */
      }
    },
  };
  snapshot(visible ? "visible" : "hidden");
  schedule();
  return observer;
}

export function browserEngagementEnvironment(): EngagementEnvironment | undefined {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof performance === "undefined"
  )
    return undefined;
  return {
    monotonicNow: () => performance.now(),
    visible: () => document.visibilityState === "visible",
    schedule(listener, ms) {
      const timer = setTimeout(listener, ms);
      return () => clearTimeout(timer);
    },
    observe(listener) {
      const visibility = () =>
        listener(document.visibilityState === "visible" ? "visible" : "hidden");
      const hide = (event: PageTransitionEvent) => listener("pagehide", event.persisted);
      const show = () => {
        if (document.visibilityState === "visible") listener("visible");
      };
      document.addEventListener("visibilitychange", visibility);
      window.addEventListener("pagehide", hide);
      window.addEventListener("pageshow", show);
      return () => {
        document.removeEventListener("visibilitychange", visibility);
        window.removeEventListener("pagehide", hide);
        window.removeEventListener("pageshow", show);
      };
    },
  };
}
